import moment from "moment-timezone";
import { CacheObject, EditDaySessions, SessionEvent } from "./types";
import { get } from "http";
import CryptoJS from "crypto-js";
import { maxNationalLength } from "./countryCodes";

type FormattedDate = {
  tareek: number; // Day of the month as a number
  day: string; // Abbreviated day of the week, e.g., 'Mon', 'Tue'
  date: string; // Full date in 'YYYY-MM-DD' format
};

function getFormattedDay(date: moment.Moment): string {
  return date.format("ddd"); // 'ddd' formats the day as an abbreviated string, e.g., 'Mon', 'Tue'.
}

export function getDatesAroundToday(offset: number = 0): FormattedDate[] {
  const dates: FormattedDate[] = [];

  // Start from the offset
  for (let i = offset - 2; i <= offset + 2; i++) {
    const targetDate: moment.Moment = moment().add(i, "days"); // Add i days to the current date

    const formattedDate: FormattedDate = {
      tareek: targetDate.date(), // Gets the day of the month
      day: getFormattedDay(targetDate), // Use the function to get the formatted day
      date: targetDate.format("YYYY-MM-DD"), // Formats the date as a string, e.g., '2024-02-12'
    };

    dates.push(formattedDate);
  }
  return dates;
}

export function convertToMinutes(timeStr: string): number {
  const parsedTime = moment(timeStr, "hh:mm A");
  return parsedTime.hours() * 60 + parsedTime.minutes();
}

// Digits expected after the country code. Longest valid mobile length from the
// shared country list (India/Iraq 10, UAE 9, Qatar 8 as before); 10 if unknown.
export function getCountryNumberLength(code: string): number {
  return maxNationalLength(code);
}

export function getDatesFromSunToSat(weekOffset: number = 0) {
  const dates = [];
  const currentDate = moment().add(weekOffset, "weeks");

  for (let i = 0; i < 7; i++) {
    const targetDate = currentDate.clone().startOf("week").add(i, "days");

    const formattedDate = {
      tareek: targetDate.date(),
      day: targetDate.format("ddd"),
      date: targetDate.format("YYYY-MM-DD"),
      month: targetDate.format("MMMM"),
    };

    dates.push(formattedDate);
  }
  return dates;
}

export type TimeOfDay =
  | "morning"
  | "afternoon"
  | "evening"
  | "morningEnd"
  | "afternoonEnd"
  | "eveningEnd";

export const generateTimeOptions = (
  timeOfDay: TimeOfDay | string,
  prevEndTime?: string
) => {
  const times = [];
  const format = "hh:mm A";

  let startTime, endTime;

  switch (timeOfDay) {
    case "morning":
      startTime = moment().startOf("day"); // up to 6 AM
      endTime = moment().endOf("day"); // up to 12 PM
      break;
    case "afternoon":
      startTime = moment().startOf("day").hour(12); // starting from 12 PM
      endTime = moment().endOf("day"); // up to 5 PM
      break;
    case "evening":
      startTime = moment().startOf("day").hour(17); // starting from 5 PM
      endTime = moment().endOf("day"); // until the end of the day
      break;
    case "morningEnd":
      startTime = moment().startOf("day");
      endTime = moment().startOf("day").hour(23).minute(59); // up to 11:59 PM
      break;
    case "afternoonEnd":
      startTime = moment().startOf("day").hour(12); // starting from 12 PM
      endTime = moment().startOf("day").hour(23).minute(59); // up to 11:59 PM
      break;
    case "eveningEnd":
      startTime = moment().startOf("day").hour(17); // starting from 5 PM
      endTime = moment().startOf("day").hour(23).minute(59); // up to 11:59 PM
      break;
    default:
      throw new Error("Invalid time of day");
  }

  // Override startTime with prevEndTime if provided
  if (prevEndTime) {
    startTime = moment(prevEndTime, "HH:mm:ss").add(0, "minutes");
  }

  let currentTime = startTime;

  while (currentTime.isBefore(endTime)) {
    times.push(currentTime.format(format));
    currentTime.add(30, "minutes");
  }
  // If the last time exceeds 11:59 PM in "eveningEnd", replace it with 11:59 PM
  if (
    timeOfDay === "eveningEnd" &&
    times[times.length - 1] !== moment(endTime).format(format)
  ) {
    times[times.length - 1] = moment(endTime).format(format);
  }

  return times;
};

export const updateAvailability = (
  weekSchedule: {
    [key: number]: EditDaySessions;
  },
  mapping_id: string
) => {
  const finalData = [];

  // console.log("weekSchedule", JSON.stringify(weekSchedule));

  for (let day in weekSchedule) {
    const eachDay = weekSchedule[day];

    if (eachDay.selected === true) {
      for (let session in eachDay.sessions)
        if (eachDay.sessions[session].selected === true) {
          const eachSession = eachDay.sessions[session];
          if (
            eachSession.start_time === "" ||
            eachSession.end_time === "" ||
            eachSession.wait_time.minutes === 0 ||
            eachSession.max_patients_per_slot === 0 ||
            eachSession.queue_type === "" ||
            eachSession.max_wa_tokens === 0
          ) {
            console.log(undefined);
          } else {
            console.log("eachSession.wait_time", eachSession.wait_time);
            const modifiedSession = {
              ...eachSession,
              wait_time: `${
                eachSession.wait_time.hours &&
                eachSession.wait_time.minutes === undefined
                  ? eachSession.wait_time.hours * 60
                  : eachSession.wait_time.hours && eachSession.wait_time.minutes
                  ? eachSession.wait_time.hours * 60 +
                    eachSession.wait_time.minutes
                  : eachSession.wait_time.minutes
                  ? eachSession.wait_time.minutes
                  : eachSession.wait_time
              } minutes`,
              is_active: true,
              mapping_id: mapping_id,
            };
            if (eachSession.availability_id === "") {
              const { selected, availability_id, ...rest } = modifiedSession;
              finalData.push(rest);
            } else {
              const { selected, ...rest } = modifiedSession;
              finalData.push(rest);
            }
          }
        }
    }
  }
  console.log(finalData);
  return finalData;
};

export const addAvailability = (weekSchedule: {
  [key: number]: EditDaySessions;
}) => {
  const finalData = [];
  for (let day in weekSchedule) {
    const eachDay = weekSchedule[day];

    if (eachDay.selected === true) {
      for (let session in eachDay.sessions)
        if (eachDay.sessions[session].selected === true) {
          const eachSession = eachDay.sessions[session];
          if (
            eachSession.availability_id !== "" ||
            eachSession.start_time === "" ||
            eachSession.end_time === "" ||
            eachSession.wait_time.minutes === 0 ||
            eachSession.max_patients_per_slot === 0
          ) {
          } else {
            const modifiedSession = {
              ...eachSession,
              wait_time: `${
                eachSession.wait_time.minutes || eachSession.wait_time
              } minutes`,
            };
            const { selected, availability_id, ...rest } = modifiedSession;
            finalData.push(rest);
          }
        }
    }
  }
  return finalData;
};

export function momentIN(dateString?: string) {
  return moment.tz(dateString, "Asia/Kolkata");
}

export const generateTimings = (
  slot: string | undefined,
  date: string | undefined
) => {
  const format = "hh:mm A";
  const slots = [];

  // Assuming the date is passed in the format "YYYY-MM-DD hh:mm A - hh:mm A"
  const datePart = date; // Extract the date part
  const today = moment().format("YYYY-MM-DD"); // Format today's date in the same format as the date part

  if (datePart === today) {
    // If the slot's date is today, check the start time
    let startMoment = moment(slot?.split(" - ")[0], "hh:mm A");
    const endMoment = moment(slot?.split(" - ")[1], "hh:mm A");
    const currentMoment = moment(); // Get the current moment

    if (startMoment.isBefore(currentMoment)) {
      // If the start time has already passed, adjust the startMoment to the next half-hour mark
      const minutes = currentMoment.minute();
      if (minutes < 30) {
        startMoment = currentMoment.minute(0).second(0);
      } else {
        startMoment = currentMoment.minute(30).second(0);
      }
    }

    while (startMoment.isBefore(endMoment)) {
      const endSlot = startMoment.clone().add(30, "minutes"); // Create a new moment for the end of the slot
      slots.push(`${startMoment.format(format)} - ${endSlot.format(format)}`);
      startMoment = endSlot; // Move the start to the end of the current slot for the next iteration
    }
  } else {
    // If the date is not today, generate slots as before
    let startMoment = moment(slot?.split(" - ")[0], "hh:mm A");
    const endMoment = moment(slot?.split(" - ")[1], "hh:mm A");

    while (startMoment.isBefore(endMoment)) {
      const endSlot = startMoment.clone().add(30, "minutes");
      slots.push(`${startMoment.format(format)} - ${endSlot.format(format)}`);
      startMoment = endSlot;
    }
  }

  return slots;
};

export function parseTime(timeStr: string) {
  const [hours, minutes, seconds] = timeStr.split(":").map(Number);
  const date = moment().set({
    hour: hours,
    minute: minutes,
    second: seconds,
    millisecond: 0,
  });
  return date.toDate();
}

export const formatTime = (date: Date) => {
  const formattedTime = moment(date).format("hh:mm A");
  return formattedTime;
};

export function divideTimeIntoSections(
  startTime: string,
  endTime: string,
  number_of_people: number,
  atpp: number
) {
  console.log(startTime, endTime, number_of_people, atpp);
  const sections = [];
  const start = parseTime(startTime);
  const end = parseTime(endTime);

  // Calculate the time difference in hours
  let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // Convert milliseconds to hours
  // Decide on the increment based on the time difference
  let increment = diff >= 5 ? 60 : 30; // 60 minutes for >= 5 hours difference, otherwise 30 minutes
  increment =
    increment > number_of_people * atpp
      ? increment
      : Math.round((number_of_people * atpp) / 10) * 10;

  while (start < end) {
    const sectionStart = formatTime(start);
    start.setMinutes(start.getMinutes() + increment); // Increment by decided minutes
    const sectionEnd = start <= end ? formatTime(start) : formatTime(end);
    sections.push(`${sectionStart} - ${sectionEnd}`);
  }

  return sections;
}

export const generateRandomString = (length: number) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const playSound = () => {
  const audio = new Audio(require("../../assets/audio/nextPatientLong.opus"));
  audio
    .play()
    .catch((error) => console.error("Error playing the sound:", error));
};

export const getRouteSegment = (path: number) => {
  const pathSegments = window.location.pathname.split("/");
  return pathSegments[path] || "";
};

export const getDate = (date: string) => {
  // Convert the date to IST and format it as "DD/MM/YYYY"
  if(!date) return '';
  const dateInIST = new Date(date).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata", // IST time zone
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Split the formatted date to rearrange to "YYYY-MM-DD"
  const [day, month, year] = dateInIST.split("/");
  return `${day}-${month}-${year}`;
};

export function convertTimeTo24(timeStr:string) {
  if (!timeStr) return null;
  // Match patterns like "9 pm", "9:30 pm", "12 am", etc.
  const regex = /^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/i;
  const match = timeStr.trim().match(regex);
  if (!match) {
    throw new Error(`Invalid time format: ${timeStr}`);
  }
  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridian = match[3].toLowerCase();

  if (meridian === 'pm' && hours !== 12) {
    hours += 12;
  } else if (meridian === 'am' && hours === 12) {
    hours = 0;
  }
  // Return in HH:MM:SS format
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
}

export const isFirebaseStorageUrl = (url: string | undefined): boolean => {
  const firebaseStorageRegex =
    /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^\/]+\/o\/[^\/]+$/;
    
  return firebaseStorageRegex.test(String(url));
};

// function to convert session to sessionEvent
export const convertSessionToSessionEvent = function (
  session: any
): SessionEvent {
  const { availability_id, day_of_week, start_time, end_time } = session;

  // Calculate the start of the current week (Sunday)
  const startOfWeek = moment().startOf("week").add(0, "days"); // Adjust to make Sunday the first day of the week

  // Calculate the date based on the current week and day_of_week
  const date = startOfWeek.add(day_of_week - 1, "days").format("YYYY-MM-DD");

  // Format the start and end times
  const formattedStartTime = moment(start_time, "HH:mm:ss").format("hh:mmA");
  const formattedEndTime = moment(end_time, "HH:mm:ss").format("hh:mmA");

  // Create the new object
  const event: SessionEvent = {
    id: date,
    title: `${formattedStartTime} - ${formattedEndTime}`,
    start: new Date(
      moment(`${date} ${start_time}`, "YYYY-MM-DD HH:mm:ss").toISOString()
    ),
    end: new Date(
      moment(`${date} ${end_time}`, "YYYY-MM-DD HH:mm:ss").toISOString()
    ),
    resourceId: availability_id,
    unavailable: false,
  };

  return event;
};

// function to convert sessions to sessionEvents
export const convertSessionsToSessionEvents = function (
  sessions: any
): SessionEvent[] {
  return sessions.map((session: any) => convertSessionToSessionEvent(session));
};

export function getMaxWaTokens(value: string | undefined, maxPatients: number) {
  switch (value) {
    case "Even":
      return Math.floor(maxPatients / 2);
    case "Odd":
      return Math.ceil(maxPatients / 2);
    case "Multiple3":
      return Math.floor(maxPatients / 3);
    default:
      return maxPatients;
  }
}

export function getWaitTimeInMinutes(waitTime: any) {
  let res = 0;
  if (waitTime === null) {
    return res;
  } else {
    if (waitTime?.hours) {
      res = Number(waitTime.hours) * 60;
    }

    if (waitTime?.minutes) {
      res += Number(waitTime.minutes);
    }
  }

  return res;
}

// Decryption function for AES-256-CBC using CryptoJS in a browser
export const decrypt = (encryptedHex:string) => {
  // Read the secret key and IV from your environment variables
  // (you can store these in your .env file prefixed with REACT_APP_ for Create React App)
  const secretKeyHex = process.env.REACT_APP_CRYPTO_SECRETKEY;
  const ivHex = process.env.REACT_APP_CRYPTO_IV;

  // Convert the key and IV from hex strings to CryptoJS WordArray objects
  const key = CryptoJS.enc.Hex.parse(secretKeyHex||"");
  const iv = CryptoJS.enc.Hex.parse(ivHex||"");

  // Convert the encrypted hex string to a CipherParams object
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: CryptoJS.enc.Hex.parse(encryptedHex)
  });

  // Decrypt using AES-256-CBC with CBC mode and PKCS7 padding
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Convert the decrypted WordArray to a UTF-8 string
  return decrypted.toString(CryptoJS.enc.Utf8);
};

/**
 * Convert a UTC date/time string (or moment) to a specific timezone.
 *
 * @param utcDate    - A UTC date/time string (e.g. '2025-05-01T12:00:00Z') or a moment object
 * @param timezone   - IANA timezone name (e.g. 'Asia/Kolkata', 'America/New_York')
 * @param format     - Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns          - Formatted date/time string in the target timezone
 */
export function utcToTimezone(
  utcDate: string | moment.Moment | Date,
  timezone: string,
  format: string = "YYYY-MM-DD HH:mm:ss"
): string {
  // moment.utc handles strings, Dates, or Moments just fine
  const m = moment.utc(utcDate);
  return m.tz(timezone).format(format);
}


/**
 * Convert a date/time string in a specific timezone to a UTC timestamp.
 *
 * @param localDate  - A date/time string in the given timezone (e.g. '2025-05-01 17:30:00')
 * @param timezone   - IANA timezone name that `localDate` is in
 * @param format     - Input format of localDate (default: 'YYYY-MM-DD HH:mm:ss')
 * @param outFormat  - Output format in UTC (default: 'YYYY-MM-DDTHH:mm:ss[Z]')
 * @returns          - Formatted UTC date/time string
 */
export function timezoneToUtc(
  localDate: string,
  timezone: string,
  format: string = "YYYY-MM-DD HH:mm:ss",
  outFormat: string = "YYYY-MM-DDTHH:mm:ss[Z]"
): string {
  // Parse in the given timezone, then convert to UTC
  return moment.tz(localDate, format, timezone).utc().format(outFormat);
}

/**
 * Convert a UTC time string ("HH:mm:ss") to a specific timezone.
 *
 * @param utcTime       - A time string in UTC, e.g. "14:30:00"
 * @param timezone      - IANA timezone name, e.g. "Asia/Kolkata"
 * @param inputFormat   - Format of the incoming time string (default: "HH:mm:ss")
 * @param outputFormat  - Desired output format (default: "HH:mm:ss")
 * @returns             - Formatted time string in the target timezone
 */
export function utcTimeToTimezone(
  utcTime: string,
  timezone: string,
  inputFormat: string  = "HH:mm:ss",
  outputFormat: string = "HH:mm:ss",
  invertMeridiem: boolean = false    // ← new flag
): string {
  // 1) parse the dummy date in UTC
  const dummyDate  = `1970-01-01 ${utcTime}`;
  const parseFormat = `YYYY-MM-DD ${inputFormat}`;

  // 2) shift into the target zone & format
  let formatted = moment
    .utc(dummyDate, parseFormat)
    .tz(timezone)
    .format(outputFormat);

  // 3) optionally flip AM/PM
  if (invertMeridiem && /A/.test(outputFormat)) {
    formatted = formatted
      .replace(/AM$/, "PM")
      .replace(/PM$/, "AM");
  }

  return formatted;
}

/**
 * Convert a local time string in a specific timezone back to UTC.
 *
 * @param localTime     - A time string in the given timezone, e.g. "20:00:00"
 * @param timezone      - IANA timezone name that `localTime` is in
 * @param inputFormat   - Format of the incoming time string (default: "HH:mm:ss")
 * @param outputFormat  - Desired output format in UTC (default: "HH:mm:ss")
 * @returns             - Formatted UTC time string
 */
export function timezoneTimeToUtc(
  localTime: string,
  timezone: string,
  inputFormat: string = "HH:mm:ss",
  outputFormat: string = "HH:mm:ss"
): string {
  const dummyDate = `1970-01-01 ${localTime}`;
  const parseFormat = `YYYY-MM-DD ${inputFormat}`;

  // Parse in the given zone, then convert to UTC
  return moment
    .tz(dummyDate, parseFormat, timezone)
    .utc()
    .format(outputFormat);
}


/**
 * Convert a UTC date string ("YYYY-MM-DD") to a specific timezone.
 *
 * @param utcDate       - A date string in UTC, e.g. "2025-05-01"
 * @param timezone      - IANA timezone name, e.g. "Asia/Kolkata"
 * @param inputFormat   - Format of the incoming date string (default: "YYYY-MM-DD")
 * @param outputFormat  - Desired output format (default: "YYYY-MM-DD")
 * @returns             - Formatted date string in the target timezone
 */
export function utcDateToTimezone(
  utcDate: string,
  timezone: string,
  inputFormat: string = "YYYY-MM-DD",
  outputFormat: string = "YYYY-MM-DD"
): string {
  // Parse the UTC date and shift it to the target timezone
  return moment
    .utc(utcDate, inputFormat)
    .tz(timezone)
    .format(outputFormat);
}

/**
 * Convert a local date string in a specific timezone to UTC.
 *
 * @param localDate     - A date string in the given timezone, e.g. "2025-05-01"
 * @param timezone      - IANA timezone name, e.g. "Asia/Kolkata"
 * @param inputFormat   - Format of the incoming date string (default: "YYYY-MM-DD")
 * @param outputFormat  - Desired output format in UTC (default: "YYYY-MM-DD")
 * @returns             - Formatted UTC date string
 */
export function timezoneDateToUtc(
  localDate: string,
  timezone: string,
  inputFormat: string = "YYYY-MM-DD",
  outputFormat: string = "YYYY-MM-DD"
): string {
  // Parse the local date in the given timezone and convert it to UTC
  return moment
    .tz(localDate, inputFormat, timezone)
    .utc()
    .format(outputFormat);
}




export  function getAllowedStartTimeRangeAndTimeOptions(
  events: SessionEvent[],
  selectedEvent: SessionEvent | undefined,
  currentEndTime: string | undefined
) {
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const selectedIndex = sortedEvents.findIndex(
    (e) => e.resourceId === selectedEvent?.resourceId
  );

  const prev = sortedEvents[selectedIndex - 1];
  const minStartMoment = prev ? moment(prev.end) : moment(selectedEvent?.start).startOf("day");
  const maxStartMoment =  moment(selectedEvent?.end)

  // If currentEndTime is provided, update the time part of maxStartMoment
  if (currentEndTime) {
    const {hours, minutes} = moment(currentEndTime, "hh:mmA").toObject();
    maxStartMoment.set({
      hour: hours,
      minute: minutes,
    });
  }

    
  const minStartTime = minStartMoment.format("hh:mm A");
  const maxStartTime = maxStartMoment.format("hh:mm A");


  const startTimeOptions: string[] = [];
  let temp = minStartMoment.clone();

  while (temp.isBefore(maxStartMoment)) {
    startTimeOptions.push(temp.format("hh:mm A"));
    temp = temp.clone().add(30, "minutes");
  }
  
  console.log("startTimeOptions", startTimeOptions);


  return { minStartTime, maxStartTime, startTimeOptions };
}

export function getAllowedEndTimeRangeAndTimeOptions(
  events: SessionEvent[],
  selectedEvent: SessionEvent | undefined,
  currentStartTime: string | undefined
){
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const selectedIndex = sortedEvents.findIndex(
    (e) => e.resourceId === selectedEvent?.resourceId
  );

  const next = sortedEvents[selectedIndex + 1];
  const minEndMoment =  moment(selectedEvent?.start)
  const maxEndMoment = next ? moment(next.start) : moment(selectedEvent?.start).endOf("day");

  // If currentStartTime is provided, update the time part of minEndMoment
  if (currentStartTime) {
    const {hours, minutes} = moment(currentStartTime, "hh:mmA").toObject();
    minEndMoment.set({
      hour: hours,
      minute: minutes,
    });
  }

    
  const minEndTime = minEndMoment.format("hh:mm A");
  const maxEndTime = maxEndMoment.format("hh:mm A");


  const endTimeOptions: string[] = [];
  let temp = minEndMoment.clone().add(30, "minutes");

  while (temp.isSameOrBefore(maxEndMoment)) {
    endTimeOptions.push(temp.format("hh:mm A"));
    temp = temp.clone().add(30, "minutes");
  }

  console.log("endTimeOptions", endTimeOptions);

  return { minEndTime, maxEndTime, endTimeOptions };
}

export function getDateBeforeXDays(X:number){
  const date = new Date();
    date.setDate(date.getDate() - X);
    return date;
}

export function getISODateFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}


export class CacheWithExpiration {
  static set(key: string, data: any, expirationInMinutes: number = 60): void {
    const expirationTime = new Date().getTime() + (expirationInMinutes * 60 * 1000);
    const cacheObject: CacheObject = {
      data: data,
      expiration: expirationTime
    };
    localStorage.setItem(key, JSON.stringify(cacheObject));
  }

  static get(key: string): any | null {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return null;
    }

    try {
      const cacheObject: CacheObject = JSON.parse(cached);
      const now = new Date().getTime();

      if (now > cacheObject.expiration) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheObject.data;
    } catch (error) {
      // Handle JSON parse errors
      localStorage.removeItem(key);
      return null;
    }
  }

  static remove(key: string): void {
    localStorage.removeItem(key);
  }

  static clear(): void {
    localStorage.clear();
  }
}


export const getFirstLastDateOfYear = (year: number) => {
    const startDate = new Date(year, 0, 1); // January 1st
    const endDate = new Date(year, 11, 31); // December 31st

    return {
      start_date: getISODateFromDate(startDate), // Returns "2025-01-01"
      end_date: getISODateFromDate(endDate)      // Returns "2025-12-31"
    };
};
export function getAge(dob: string): number {
  return moment().diff(moment(dob), 'years');
}

// Token refresh is handled by the apiClient interceptor in fetch.ts (with mutex).
// This wrapper just calls the API function directly — no duplicate refresh needed.
export const handleApiCall = async (apiFn: Function, params: any) => {
  return await apiFn(params);
};
